import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { ButtonBase } from '@material-ui/core';
import { useMeContext } from '~/modules/me/useMeContext';
import { useHasPermission } from '~/modules/common/permissions';
import { ResourceRequestStatus } from '~/types';
import AssignResourceButton from '~/modules/resourcing/common/components/AssignResourceButton';
import IconLinkButton from '~/modules/resourcing/common/components/IconLinkButton';
import { Percentage, User, NoDataItem } from '~/modules/common/components';
import { useRedirectToAssignResource } from '~/modules/resourcing/hooks';
import { getReactNumberFormatFromMe } from '~/modules/common/numbers';
import useStyles from './useStyles';

export const ReadOnlyResourceAllocationUserItem = ({
  allocation,
  numberSettings,
  classes,
  iconLinkButtonClasses,
  cssClasses
}) => {
  const intl = useIntl();
  const history = useHistory();
  const user = get(allocation, 'user.user');
  const isViewUserEnabled =
    user.permittedActionUris &&
    user.permittedActionUris.includes('urn:replicon:user-action:view-user');
  const toLink = `#/resource/${user.slug}`;

  const loadValue = parseFloat(
    (allocation.load || 0).toFixed(numberSettings.decimalScale || 2)
  );

  const loadingToolTip = useMemo(
    () => (
      <div>
        {intl.formatMessage({
          id: 'resourceRequestReadonlyItem.loading'
        })}
        : ${loadValue}%
        {allocation.isAdjustedLoading && (
          <div className={classes.tooltipDetails}>
            {intl.formatMessage({
              id: 'resourceRequestReadonlyItem.averageLoading'
            })}
          </div>
        )}
      </div>
    ),
    [intl, loadValue, allocation.isAdjustedLoading, classes.tooltipDetails]
  );

  const openDrawer = useCallback(() => history.push(toLink), [history, toLink]);

  return (
    <ButtonBase key={user.uri} className={classes.details} onClick={openDrawer}>
      <div className={classes.resource}>
        <User user={user} classes={cssClasses} />
      </div>
      <div className={classes.container}>
        <span className={classes.atDivider}>@</span>
        <Percentage
          title={loadingToolTip}
          value={allocation.load}
          className={classes.loading}
          fixedDecimalScale={false}
        />
        {allocation.isAdjustedLoading && <span> * </span>}
      </div>
      {isViewUserEnabled && (
        <IconLinkButton
          classes={iconLinkButtonClasses}
          toLink={toLink}
          ariaLabel={intl.formatMessage({
            id: 'resourceRequestReadonlyItem.showUserDetails'
          })}
        />
      )}
    </ButtonBase>
  );
};

ReadOnlyResourceAllocationUserItem.propTypes = {
  allocation: PropTypes.object,
  numberSettings: PropTypes.object,
  classes: PropTypes.object,
  iconLinkButtonClasses: PropTypes.object,
  cssClasses: PropTypes.object
};

export const ReadOnlyResourceAllocationItem = ({
  classes: classesOverrides,
  resourceRequest,
  allocations = [],
  showResourceAllocation,
  isEditSwitchEnabled
}) => {
  const classes = useStyles({
    classes: { openButton: classesOverrides.openButton },
    isEditSwitchEnabled
  });
  const intl = useIntl();
  const history = useHistory();

  const { locale } = useMeContext();

  const onAssignResourceClick = useRedirectToAssignResource({
    history,
    resourceRequest,
    isResourcingTab: false
  });

  const isResourceAssignmentEnabled = useHasPermission({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });

  const cssClasses = useMemo(() => ({ name: classes.userName }), [
    classes.userName
  ]);

  const iconLinkButtonClasses = useMemo(
    () => ({ openButton: classes.openButton }),
    [classes.openButton]
  );

  const numberSettings = getReactNumberFormatFromMe({ locale });

  const isNotDraftOrCompleteStatusRequest =
    resourceRequest.requestStatus !== ResourceRequestStatus.Draft &&
    resourceRequest.requestStatus !== ResourceRequestStatus.Complete;

  return showResourceAllocation && allocations.length !== 0 ? (
    <div className={classes.root}>
      {allocations.map(allocation => (
        <ReadOnlyResourceAllocationUserItem
          key={allocation.id}
          intl={intl}
          allocation={allocation}
          numberSettings={numberSettings}
          classes={classes}
          iconLinkButtonClasses={iconLinkButtonClasses}
          cssClasses={cssClasses}
        />
      ))}
    </div>
  ) : isEditSwitchEnabled &&
    isResourceAssignmentEnabled &&
    isNotDraftOrCompleteStatusRequest ? (
    <div className={classes.allocation}>
      <AssignResourceButton
        quantity={resourceRequest.quantity}
        onClick={onAssignResourceClick}
      />
    </div>
  ) : (
    isNotDraftOrCompleteStatusRequest && (
      <div className={classes.noResourcesMessage}>
        <NoDataItem>
          <FormattedMessage id="readOnlyResourceAllocationItem.noResourcesAllocated" />
        </NoDataItem>
      </div>
    )
  );
};

ReadOnlyResourceAllocationItem.propTypes = {
  allocations: PropTypes.array,
  classes: PropTypes.object,
  resourceRequest: PropTypes.object,
  isEditSwitchEnabled: PropTypes.bool,
  showResourceAllocation: PropTypes.bool
};

export default ReadOnlyResourceAllocationItem;
